/**
 * Lifecycle hook for the paid-media CSV export.
 *
 * The backend export is async: POST starts an Athena query and returns an id; start()
 * then polls that id in a single linear loop (one request at a time) until the export
 * completes, fails, or the timeout budget runs out. Download re-fetches for a fresh
 * presigned URL.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLazyGetPaidMediaExportStatusQuery,
  useStartPaidMediaExportMutation,
  type ExportStatusResponse,
  type PaidMediaExportArgs,
} from 'src/store/leela-api';

export const POLL_INTERVAL_MS = 2000;
export const POLL_TIMEOUT_MS = 120000;

export type ExportState = 'idle' | 'exporting' | 'complete' | 'error';

export interface UseCsvExportArgs {
  view: string;
  subView: string;
  filters: Record<string, string | string[] | undefined>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface UseCsvExportResult {
  state: ExportState;
  start: () => void;
  download: () => void;
  retry: () => void;
}

const serializeExportArgs = (args: PaidMediaExportArgs): string =>
  JSON.stringify({
    view: args.view,
    subView: args.subView,
    sortBy: args.sortBy,
    sortOrder: args.sortOrder,
    filters: args.filters,
  });

const startDownload = (url: string): void => {
  window.location.assign(url);
};

export const useCsvExport = ({ view, subView, filters, sortBy, sortOrder }: UseCsvExportArgs): UseCsvExportResult => {
  const [startCsvExport] = useStartPaidMediaExportMutation();
  const [getCsvExportStatus] = useLazyGetPaidMediaExportStatusQuery();

  const [exportState, setExportState] = useState<ExportState>('idle');
  const [exportId, setExportId] = useState<string | undefined>(undefined);

  const startedArgsRef = useRef<string>('');
  // Flipped on unmount so an in-flight poll loop bails instead of firing wasted status requests
  // against a component that is no longer on screen.
  const cancelledRef = useRef(false);

  // Cleanup flips the flag so an in-flight poll bails on unmount. But when the button remounts —
  // StrictMode's dev double-mount, or the user leaving the dashboard and coming back — the prior
  // cleanup left it true; reset it to false on mount so the next export can actually poll.
  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  const currentArgs = serializeExportArgs({ view, subView, sortBy, sortOrder, filters });

  // Drop a finished result if the filters change out from under it, but leave an in-flight export alone
  useEffect(() => {
    if ((exportState === 'complete' || exportState === 'error') && currentArgs !== startedArgsRef.current) {
      setExportState('idle');
      setExportId(undefined);
    }
  }, [currentArgs, exportState]);

  const start = useCallback(async (): Promise<void> => {
    const args: PaidMediaExportArgs = { view, subView, sortBy, sortOrder, filters };
    // Record what this export was started with, so the drift effect can detect filter changes.
    startedArgsRef.current = currentArgs;

    setExportState('exporting');

    let id: string;
    try {
      ({ exportId: id } = await startCsvExport(args).unwrap());
    } catch {
      setExportState('error');
      return;
    }
    // An unmount during the initial POST means there is nothing left to poll for.
    if (cancelledRef.current) return;
    setExportId(id);

    // Poll one request at a time until a terminal state or the timeout budget runs out.
    const startedAt = Date.now();
    while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
      if (cancelledRef.current) return;
      let status: ExportStatusResponse;
      try {
        status = await getCsvExportStatus(id).unwrap();
      } catch {
        setExportState('error');
        return;
      }
      if (status.status === 'complete') {
        setExportState('complete');
        return;
      }
      if (status.status === 'failed') {
        setExportState('error');
        return;
      }
      await new Promise((resolve) => { setTimeout(resolve, POLL_INTERVAL_MS); });
    }
    setExportState('error');
  }, [currentArgs, view, subView, sortBy, sortOrder, filters, startCsvExport, getCsvExportStatus]);

  const download = useCallback(async (): Promise<void> => {
    if (exportId == null) {
      setExportState('error');
      return;
    }
    try {
      const status = await getCsvExportStatus(exportId).unwrap();
      if (status.status === 'complete' && status.url != null) {
        startDownload(status.url);
      } else {
        setExportState('error');
      }
    } catch {
      setExportState('error');
    }
  }, [exportId, getCsvExportStatus]);

  const retry = useCallback(() => {
    setExportState('idle');
    setExportId(undefined);
  }, []);

  return {
    state: exportState,
    start,
    download,
    retry,
  };
};
